import { diff } from 'deep-object-diff';
import i18n from '@/plugins/vue-i18n';
import { hasGeneralPartnerUserAddedProductManagerPayload, hasGeneralPartnerUserCreatedProductNewsArticlePayload, hasGeneralPartnerUserCreatedProductNewsArticleAttachmentPayload, hasGeneralPartnerUserCreatedSubscriptionDocumentTemplatePayload, hasGeneralPartnerUserDeletedProductPayload, hasGeneralPartnerUserDeletedProductManagerPayload, hasGeneralPartnerUserDeletedProductNewsArticlePayload, hasGeneralPartnerUserDeletedProductNewsArticleAttachmentPayload, hasGeneralPartnerUserDeletedSubscriptionDocumentTemplatePayload, hasGeneralPartnerUserDisabledDocumentManagementForSubscriptionProcessPayload, hasGeneralPartnerUserDisabledProductInvestabilityPayload, hasGeneralPartnerUserDownloadedProductNewsArticleAttachmentPayload, hasGeneralPartnerUserEnabledDocumentManagementForSubscriptionProcessPayload, hasGeneralPartnerUserEnabledProductInvestabilityPayload, hasGeneralPartnerUserLoggedInPayload, hasGeneralPartnerUserPublishedProductPayload, hasGeneralPartnerUserUnpublishedProductPayload, hasGeneralPartnerUserUpdatedKYCInstructionPayload, hasGeneralPartnerUserUpdatedProductContactEmailAddressPayload, hasGeneralPartnerUserUpdatedProductDescriptionPayload, hasGeneralPartnerUserUpdatedProductManagerPayload, hasGeneralPartnerUserUpdatedProductManagersOrderPayload, hasGeneralPartnerUserUpdatedProductNamePayload, hasGeneralPartnerUserUpdatedProductNewsArticlePayload, hasGeneralPartnerUserUpdatedSubscriptionInstructionPayload, hasGeneralPartnerUserUpdatedTrackRecordImagePayload, hasGeneralPartnerUserDeletedTrackRecordImagePayload, hasInvestorUserAcceptedInvitationPayload, hasInvestorUserDownloadedDataRoomPayload, hasInvestorUserDownloadedDirectoryInDataRoomPayload, hasInvestorUserDownloadedFileInDataRoomPayload, hasInvestorUserLoggedInPayload, hasUserConfirmedNewEmailAddressPayload, hasUserRequestedNewEmailAddressPayload, hasUserUpdatedOwnCountryOfResidencePayload, hasUserUpdatedOwnLanguagePayload, hasUserUpdatedOwnNamePayload, hasUserUpdatedOwnPhoneNumberPayload, TranslatedText, UserActivityLogEntryPayload, UserActivityLogEntryType, UserActivityLogEntryTypes, hasGeneralPartnerUserAcceptedInvitationPayload, hasGeneralPartnerUserInvitedInvestorUserPayload, hasGeneralPartnerUserInvitedGeneralPartnerUserPayload, hasGeneralPartnerUserUpdatedProductImagePayload, hasGeneralPartnerUserCreatedVimeoVideoPayload, hasGeneralPartnerUserDeletedProductVideoPayload, hasGeneralPartnerUserAddedProductSummaryItemPayload, hasGeneralPartnerUserRemovedProductSummaryItemPayload, hasGeneralPartnerUserUpdatedGeneralPartnerUsersWithAccessForProductPayload, hasGeneralPartnerUserUpdatedInvestorsWithAccessForProductPayload, hasGeneralPartnerUserUpdatedProductPreviewPayload, hasGeneralPartnerUserUpdatedProductReportPayload, hasGeneralPartnerUserCreatedProductReportPayload, hasGeneralPartnerUserDownloadedProductReportPayload, hasGeneralPartnerUserDeletedProductReportPayload, hasGeneralPartnerUserCreatedDueDiligenceDirectoryPayload, hasGeneralPartnerUserCreatedDueDiligenceFilePayload, hasGeneralPartnerUserUpdatedDueDiligenceFilePayload, hasGeneralPartnerUserUploadedDueDiligenceFileWithPathPayload, hasGeneralPartnerUserUpdatedDueDiligenceDirectoryNamePayload, hasGeneralPartnerUserDownloadedDueDiligenceDirectoryPayload, hasGeneralPartnerUserDownloadedDueDiligenceFilePayload, hasGeneralPartnerUserDeletedDueDiligenceDirectoryPayload, hasGeneralPartnerUserDeletedDueDiligenceFilePayload, hasGeneralPartnerUserPinnedDueDiligenceFilePayload, hasGeneralPartnerUserUnpinnedDueDiligenceFilePayload, hasGeneralPartnerUserChangedDueDiligenceFilesOrderPayload, hasGeneralPartnerUserCreatedProductTermSheetCategoryPayload, hasGeneralPartnerUserDeletedProductTermSheetCategoryPayload, hasGeneralPartnerUserUpdatedProductTermSheetCategoryPayload, hasGeneralPartnerUserSharedProductPayload, KPI, Languages, hasGeneralPartnerUserUnsharedProductPayload, hasInvestorUserAcceptedProductNDAPayload, hasGeneralPartnerUserImpersonatedInvestorUserPayload, hasGeneralPartnerUserCreatedSubscriptionAgreementTemplateToPrefillPayload, hasGeneralPartnerUserDeletedSubscriptionAgreementTemplateToPrefillPayload, hasGeneralPartnerUserDeletedControllingPersonTemplateToPrefillPayload, hasGeneralPartnerUserCreatedControllingPersonTemplateToPrefillPayload } from '@/types/global';
import { translateWithFallback } from '@/filters/translation';
import { useAppStore } from '@/application/common/app/store';
import { getLocalizedCountryNameForCountryCode } from '@/helpers/country-helpers';
import { getFormattedInvestorNamesIntoLine } from '@/helpers/investor-name-helper';
import { getFormattedNamesFromNamesIntoLine } from '@/helpers/name-helpers';
import { languageName } from '@/helpers/language-helpers';
import { getFormattedProductReportsIntoLine } from '@/helpers/product-report-helper';
import { isValueAvailable } from '@/helpers/value-helpers';
import { formatStringArrayAsSingleStringWithBreaks } from '@/helpers/format-helpers';
import { getFormattedKPIValueForCurrency } from '@/helpers/kpi-helpers';

export function userActivityLogEntryDescription(
  userActivityLogEntry: { type: UserActivityLogEntryType, payload?: UserActivityLogEntryPayload }
): string {
  const appStore = useAppStore();

  if (hasGeneralPartnerUserLoggedInPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.applicationType}`) as string;
  }

  if (hasGeneralPartnerUserAcceptedInvitationPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      firstName: userActivityLogEntry.payload.generalPartnerUserName.firstName,
      lastName: userActivityLogEntry.payload.generalPartnerUserName.lastName,
    }) as string;
  }

  if (hasGeneralPartnerUserInvitedGeneralPartnerUserPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      firstName: userActivityLogEntry.payload.generalPartnerUserName.firstName,
      lastName: userActivityLogEntry.payload.generalPartnerUserName.lastName,
      invitedEmailAddress: userActivityLogEntry.payload.invitedEmailAddress,
    }) as string;
  }

  if (hasGeneralPartnerUserInvitedInvestorUserPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      firstName: userActivityLogEntry.payload.generalPartnerUserName.firstName,
      lastName: userActivityLogEntry.payload.generalPartnerUserName.lastName,
      invitedEmailAddress: userActivityLogEntry.payload.invitedEmailAddress,
    }) as string;
  }

  if (hasGeneralPartnerUserEnabledProductInvestabilityPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserDisabledProductInvestabilityPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedVimeoVideoPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      videoTitle: userActivityLogEntry.payload.videoTitle,
      language: languageName(userActivityLogEntry.payload.language),
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedProductVideoPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      videoTitle: userActivityLogEntry.payload.videoTitle,
      language: languageName(userActivityLogEntry.payload.language),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductContactEmailAddressPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      previousProductContactEmailAddress: userActivityLogEntry.payload.previousProductContactEmailAddress,
      newContactEmailAddress: userActivityLogEntry.payload.newContactEmailAddress,
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserPublishedProductPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUnpublishedProductPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserSharedProductPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUnsharedProductPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedProductPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductDescriptionPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      // eslint-disable-next-line max-len
      newProductDescription: compare(userActivityLogEntry.payload.previousProductDescription, userActivityLogEntry.payload.newProductDescription),
    }) as string;
  }

  if (hasGeneralPartnerUserAddedProductSummaryItemPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      kpiLabel: translateWithFallback(userActivityLogEntry.payload.kpiLabel, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserRemovedProductSummaryItemPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      kpiLabel: translateWithFallback(userActivityLogEntry.payload.kpiLabel, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductImagePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedGeneralPartnerUsersWithAccessForProductPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.updateState}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      // eslint-disable-next-line max-len
      generalPartnerUserNamesWithRemovedAccess: getFormattedNamesFromNamesIntoLine(userActivityLogEntry.payload.generalPartnerUserNamesWithRemovedAccess),
      // eslint-disable-next-line max-len
      generalPartnerUserNamesWithGrantedAccess: getFormattedNamesFromNamesIntoLine(userActivityLogEntry.payload.generalPartnerUserNamesWithGrantedAccess),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedInvestorsWithAccessForProductPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.updateState}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      investorNamesWithRemovedAccess: getFormattedInvestorNamesIntoLine(userActivityLogEntry.payload.investorNamesWithRemovedAccess),
      investorNamesWithGrantedAccess: getFormattedInvestorNamesIntoLine(userActivityLogEntry.payload.investorNamesWithGrantedAccess),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductPreviewPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      newKPILabels: compareArray(
        userActivityLogEntry.payload.previousKPILabels,
        userActivityLogEntry.payload.newKPILabels
      ),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductNamePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      newProductName: compare(userActivityLogEntry.payload.previousProductName, userActivityLogEntry.payload.newProductName),
    }) as string;
  }

  if (hasGeneralPartnerUserAddedProductManagerPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      managerName: userActivityLogEntry.payload.managerName,
      jobTitle: translateWithFallback(userActivityLogEntry.payload.jobTitle, appStore.selectedLanguage!),
      description: translateWithFallback(userActivityLogEntry.payload.description, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductManagerPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      managerName: userActivityLogEntry.payload.managerName,
      newJobTitle: compare(userActivityLogEntry.payload.previousJobTitle, userActivityLogEntry.payload.newJobTitle),
      newDescription: compare(userActivityLogEntry.payload.previousDescription, userActivityLogEntry.payload.newDescription),
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedProductManagerPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      managerName: userActivityLogEntry.payload.managerName,
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductManagersOrderPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedTrackRecordImagePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedTrackRecordImagePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedProductNewsArticlePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productNewsArticleTitle: translateWithFallback(userActivityLogEntry.payload.productNewsArticleTitle, appStore.selectedLanguage!),
      productNewsArticleContent: translateWithFallback(userActivityLogEntry.payload.productNewsArticleContent, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductNewsArticlePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      // eslint-disable-next-line max-len
      newProductNewsArticleTitle: compare(userActivityLogEntry.payload.previousProductNewsArticleTitle, userActivityLogEntry.payload.newProductNewsArticleTitle),
      // eslint-disable-next-line max-len
      newProductNewsArticleContent: compare(userActivityLogEntry.payload.previousProductNewsArticleContent, userActivityLogEntry.payload.newProductNewsArticleContent),
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedProductNewsArticlePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productNewsArticleTitle: translateWithFallback(userActivityLogEntry.payload.productNewsArticleTitle, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedProductNewsArticleAttachmentPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productNewsArticleTitle: translateWithFallback(userActivityLogEntry.payload.productNewsArticleTitle, appStore.selectedLanguage!),
      productNewsArticleAttachmentName: userActivityLogEntry.payload.productNewsArticleAttachmentName,
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedProductNewsArticleAttachmentPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productNewsArticleTitle: translateWithFallback(userActivityLogEntry.payload.productNewsArticleTitle, appStore.selectedLanguage!),
      deletedOriginalFileNames: userActivityLogEntry.payload.originalFileNames.toString(),
    }) as string;
  }

  if (hasGeneralPartnerUserDownloadedProductNewsArticleAttachmentPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productNewsArticleTitle: translateWithFallback(userActivityLogEntry.payload.productNewsArticleTitle, appStore.selectedLanguage!),
      originalFileName: userActivityLogEntry.payload.originalFileName,
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedProductReportPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.productReportType}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productReportDocuments: getFormattedProductReportsIntoLine(userActivityLogEntry.payload.productReportDocuments),
      productReportType: userActivityLogEntry.payload.productReportType,
      productReportYear: userActivityLogEntry.payload.productReportYear.value,
      productReportQuarter: userActivityLogEntry.payload.productReportQuarter?.value,
      // eslint-disable-next-line max-len
      productReportMonth: userActivityLogEntry.payload.productReportMonth && i18n.t(`helpers.data.productReportMonths.MONTH_${userActivityLogEntry.payload.productReportMonth.value}`),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductReportPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.productReportType}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productReportDocuments: getFormattedProductReportsIntoLine(userActivityLogEntry.payload.productReportDocuments),
      productReportType: userActivityLogEntry.payload.productReportType,
      productReportYear: userActivityLogEntry.payload.productReportYear.value,
      productReportQuarter: userActivityLogEntry.payload.productReportQuarter?.value,
      // eslint-disable-next-line max-len
      productReportMonth: userActivityLogEntry.payload.productReportMonth && i18n.t(`helpers.data.productReportMonths.MONTH_${userActivityLogEntry.payload.productReportMonth.value}`),
    }) as string;
  }

  if (hasGeneralPartnerUserDownloadedProductReportPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.productReportType}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productReportType: userActivityLogEntry.payload.productReportType,
      productReportYear: userActivityLogEntry.payload.productReportYear.value,
      productReportQuarter: userActivityLogEntry.payload.productReportQuarter?.value,
      // eslint-disable-next-line max-len
      productReportMonth: userActivityLogEntry.payload.productReportMonth && i18n.t(`helpers.data.productReportMonths.MONTH_${userActivityLogEntry.payload.productReportMonth.value}`),
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedProductReportPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.productReportType}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      productReportType: userActivityLogEntry.payload.productReportType,
      productReportYear: userActivityLogEntry.payload.productReportYear.value,
      productReportQuarter: userActivityLogEntry.payload.productReportQuarter?.value,
      // eslint-disable-next-line max-len
      productReportMonth: userActivityLogEntry.payload.productReportMonth && i18n.t(`helpers.data.productReportMonths.MONTH_${userActivityLogEntry.payload.productReportMonth.value}`),
    }) as string;
  }

  if (hasGeneralPartnerUserEnabledDocumentManagementForSubscriptionProcessPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserDisabledDocumentManagementForSubscriptionProcessPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedSubscriptionDocumentTemplatePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedSubscriptionDocumentTemplatePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedSubscriptionAgreementTemplateToPrefillPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedSubscriptionAgreementTemplateToPrefillPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedControllingPersonTemplateToPrefillPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedControllingPersonTemplateToPrefillPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedSubscriptionInstructionPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      // eslint-disable-next-line max-len
      newSubscriptionInstruction: compare(userActivityLogEntry.payload.previousSubscriptionInstruction, userActivityLogEntry.payload.newSubscriptionInstruction),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedKYCInstructionPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      newKYCInstruction: compare(userActivityLogEntry.payload.previousKYCInstruction, userActivityLogEntry.payload.newKYCInstruction),
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedProductTermSheetCategoryPayload(userActivityLogEntry)) {

    const addedKPIsText = userActivityLogEntry.payload.kpis.map((addedKPI) => {
      const addedKPIsTexts = [];
      addedKPIsTexts.push(translateWithFallback(addedKPI.label, appStore.selectedLanguage!));
      for (const language of Object.values(Languages)) {
        const label = translateWithFallback(addedKPI.label, language);
        const value = getFormattedKPIValueForCurrency(addedKPI, userActivityLogEntry.payload.productCurrency, false, language);
        addedKPIsTexts.push(`${language.toUpperCase()}: ${label}: ${value}`);
      }

      return formatStringArrayAsSingleStringWithBreaks(addedKPIsTexts);
    });

    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      categoryLabel: translateWithFallback(userActivityLogEntry.payload.categoryLabel, appStore.selectedLanguage!),
      addedKPIs: formatStringArrayAsSingleStringWithBreaks(addedKPIsText),
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedProductTermSheetCategoryPayload(userActivityLogEntry)) {
    const newKPIs = userActivityLogEntry.payload.newKPIs;
    const previousKPIs = userActivityLogEntry.payload.previousKPIs;

    const addedKPIs = newKPIs.filter((newKPI) => !previousKPIs.some((previousKPI) => previousKPI.kpiId === newKPI.kpiId));
    const deletedKPIs = previousKPIs.filter((previousKPI) => !newKPIs.some((newKPI) => newKPI.kpiId === previousKPI.kpiId));
    const updatedKPIs = newKPIs.filter((newKPI) => {
      const previousKPI = previousKPIs.find((previousKPI) => previousKPI.kpiId === newKPI.kpiId);
      return previousKPI && kpisHaveDifferences(previousKPI, newKPI);
    });

    const addedKPIsText = addedKPIs.map((addedKPI, kpiIndex) => {
      const addedKPIsTexts = [];
      addedKPIsTexts.push(translateWithFallback(addedKPI.label, appStore.selectedLanguage!));
      for (const language of Object.values(Languages)) {
        const label = translateWithFallback(addedKPI.label, language);
        const value = getFormattedKPIValueForCurrency(addedKPI, userActivityLogEntry.payload.newProductCurrency, false, language);
        addedKPIsTexts.push(`${language.toUpperCase()}: ${label}: ${value}`);
      }

      // Add dash to separate KPIs
      const isLastElement = kpiIndex === addedKPIs.length - 1;
      if (!isLastElement) {
        addedKPIsTexts.push('-');
      }

      return formatStringArrayAsSingleStringWithBreaks(addedKPIsTexts);
    });

    const deletedKPIsText = deletedKPIs.map((deletedKPI) => translateWithFallback(deletedKPI.label, appStore.selectedLanguage!));

    const updatedKPIsText = updatedKPIs.map((updatedKPI, kpiIndex) => {
      const previousKPI = previousKPIs.find((previousKPI) => previousKPI.kpiId === updatedKPI.kpiId);
      const updatedKPIsTexts = [];
      updatedKPIsTexts.push(translateWithFallback(previousKPI!.label, appStore.selectedLanguage!));
      for (const language of Object.values(Languages)) {
        const previousLabel = translateWithFallback(previousKPI!.label, language);
        // eslint-disable-next-line max-len
        const previousValue = getFormattedKPIValueForCurrency(previousKPI!, userActivityLogEntry.payload.previousProductCurrency, false, language);
        const newLabel = translateWithFallback(updatedKPI.label, language);
        const newValue = getFormattedKPIValueForCurrency(updatedKPI, userActivityLogEntry.payload.newProductCurrency, false, language);

        const kpiWasUpdatedTextForLanguage = `${language.toUpperCase()}: ${previousLabel}: ${previousValue} -> ${newLabel}: ${newValue}`;
        updatedKPIsTexts.push(kpiWasUpdatedTextForLanguage);
      }

      // Add dash to separate KPIs
      const isLastElement = kpiIndex === updatedKPIs.length - 1;
      if (!isLastElement) {
        updatedKPIsTexts.push('-');
      }

      return formatStringArrayAsSingleStringWithBreaks(updatedKPIsTexts);
    });

    let logText = i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.BASE`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      categoryLabel: translateWithFallback(userActivityLogEntry.payload.categoryLabel, appStore.selectedLanguage!),
    }) as string;

    const addedKPIsString = formatStringArrayAsSingleStringWithBreaks(addedKPIsText);
    if (isValueAvailable(addedKPIsString)) {
      logText += i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.ADDED_KPIS`, {
        addedKPIs: addedKPIsString,
      }) as string;
    }

    const deletedKPIsString = formatStringArrayAsSingleStringWithBreaks(deletedKPIsText);
    if (isValueAvailable(deletedKPIsString)) {
      logText += i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.DELETED_KPIS`, {
        deletedKPIs: deletedKPIsString,
      }) as string;
    }

    const updatedKPIsString = formatStringArrayAsSingleStringWithBreaks(updatedKPIsText);
    if (isValueAvailable(updatedKPIsString)) {
      logText += i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.UPDATED_KPIS`, {
        updatedKPIs: updatedKPIsString,
      }) as string;
    }

    return logText;
  }

  if (hasGeneralPartnerUserDeletedProductTermSheetCategoryPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    const translatedLabelsOfDeletedKPIs = userActivityLogEntry.payload.kpiLabels.map((kpiLabel) => translateWithFallback(kpiLabel, appStore.selectedLanguage!));

    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      categoryLabel: translateWithFallback(userActivityLogEntry.payload.categoryLabel, appStore.selectedLanguage!),
      kpiLabels: formatStringArrayAsSingleStringWithBreaks(translatedLabelsOfDeletedKPIs),
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedDueDiligenceDirectoryPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      directoryName: userActivityLogEntry.payload.directoryName,
      directoryPath: userActivityLogEntry.payload.directoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserCreatedDueDiligenceFilePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      directoryPath: userActivityLogEntry.payload.fileDirectoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedDueDiligenceFilePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      directoryPath: userActivityLogEntry.payload.fileDirectoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserUploadedDueDiligenceFileWithPathPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      directoryName: userActivityLogEntry.payload.directoryName,
      directoryPath: userActivityLogEntry.payload.fileDirectoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserUpdatedDueDiligenceDirectoryNamePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      previousDirectoryName: userActivityLogEntry.payload.previousDirectoryName,
      newDirectoryName: userActivityLogEntry.payload.newDirectoryName,
      directoryPath: userActivityLogEntry.payload.directoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserDownloadedDueDiligenceDirectoryPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      directoryName: userActivityLogEntry.payload.directoryName,
      directoryPath: userActivityLogEntry.payload.directoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserDownloadedDueDiligenceFilePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      directoryPath: userActivityLogEntry.payload.fileDirectoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedDueDiligenceDirectoryPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      directoryName: userActivityLogEntry.payload.directoryName,
      directoryPath: userActivityLogEntry.payload.directoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserDeletedDueDiligenceFilePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      directoryPath: userActivityLogEntry.payload.fileDirectoryPath,
    }) as string;
  }

  if (hasGeneralPartnerUserPinnedDueDiligenceFilePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      language: languageName(userActivityLogEntry.payload.language),
    }) as string;
  }

  if (hasGeneralPartnerUserUnpinnedDueDiligenceFilePayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
      fileName: userActivityLogEntry.payload.fileName,
      language: languageName(userActivityLogEntry.payload.language),
    }) as string;
  }

  if (hasGeneralPartnerUserChangedDueDiligenceFilesOrderPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasGeneralPartnerUserImpersonatedInvestorUserPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      firstName: userActivityLogEntry.payload.name.firstName,
      lastName: userActivityLogEntry.payload.name.lastName,
    }) as string;
  }

  if (hasInvestorUserLoggedInPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}.${userActivityLogEntry.payload.applicationType}`) as string;
  }

  if (hasInvestorUserDownloadedDataRoomPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasInvestorUserDownloadedFileInDataRoomPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      fileName: userActivityLogEntry.payload.fileName,
      folderPath: userActivityLogEntry.payload.folderPath,
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasInvestorUserDownloadedDirectoryInDataRoomPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      directoryName: userActivityLogEntry.payload.directoryName,
      folderPath: userActivityLogEntry.payload.folderPath,
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasInvestorUserAcceptedProductNDAPayload(userActivityLogEntry)) {
    // eslint-disable-next-line max-len
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      productName: translateWithFallback(userActivityLogEntry.payload.productName, appStore.selectedLanguage!),
    }) as string;
  }

  if (hasInvestorUserAcceptedInvitationPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      investorUserFirstName: userActivityLogEntry.payload.investorUserName.firstName,
      investorUserLastName: userActivityLogEntry.payload.investorUserName.lastName,
    }) as string;
  }

  if (hasUserUpdatedOwnNamePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      newSalutation: i18n.t(`helpers.data.salutations.${userActivityLogEntry.payload.newSalutation}`),
      newFirstName: userActivityLogEntry.payload.newName.firstName,
      newLastName: userActivityLogEntry.payload.newName.lastName,
    }) as string;
  }

  if (hasUserRequestedNewEmailAddressPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      newEmailAddress: userActivityLogEntry.payload.newEmailAddress,
    }) as string;
  }

  if (hasUserConfirmedNewEmailAddressPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      newEmailAddress: userActivityLogEntry.payload.newEmailAddress,
    }) as string;
  }

  if (hasUserUpdatedOwnPhoneNumberPayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      // eslint-disable-next-line max-len
      newPhoneNumber: `+${userActivityLogEntry.payload.newPhoneNumber.countryCode} ${userActivityLogEntry.payload.newPhoneNumber.phoneNumber}`,
    }) as string;
  }

  if (hasUserUpdatedOwnCountryOfResidencePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      newCountryOfResidence: getLocalizedCountryNameForCountryCode(userActivityLogEntry.payload.newCountryOfResidence),
    }) as string;
  }

  if (hasUserUpdatedOwnLanguagePayload(userActivityLogEntry)) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`, {
      newLanguage: i18n.t(`helpers.data.languages.${userActivityLogEntry.payload.newLanguage}`),
    }) as string;
  }

  // Without payload
  if (userActivityLogEntry.type === UserActivityLogEntryTypes.USER_UPDATED_OWN_PASSWORD) {
    return i18n.t(`helpers.data.userActivityLogEntryDescription.${userActivityLogEntry.type}`) as string;
  }

  throw new Error(`Invalid user activity type ${userActivityLogEntry.type}`);
}

export interface ComparableText {
  en?: string,
  de?: string,
}

export function compareArray(previousTextArray: TranslatedText[], newTextArray: TranslatedText[]) {

  const differences = diff(previousTextArray, newTextArray);
  let text = '';

  for (const difference of Object.values(differences)) {
    if (isValueAvailable(difference)) {
      text += `<br> ${textFromComparableText(difference as ComparableText)}`;
    }
  }

  return text;
}

export function compare(previousText: TranslatedText, newText: TranslatedText|TranslatedText[]) {

  const difference: ComparableText = diff(previousText, newText);
  return textFromComparableText(difference);
}

export function textFromComparableText(comparableText: ComparableText): string {
  let text = '';

  if (comparableText.en) {
    text += `EN: ${comparableText.en} `;
  }

  if (comparableText.de) {
    text += `DE: ${comparableText.de} `;
  }
  return text;
}

function kpisHaveDifferences(previousKPI: KPI, newKPI: KPI): boolean {
  const differences = diff(previousKPI, newKPI);

  return Object.values(differences).length > 0;
}
